import {Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges} from '@angular/core';
import {interval, Subject} from 'rxjs';
import {
  Dispatch,
  MapLocations,
  Program,
  ProgramSite,
  Site,
  UIDispatch,
  ZonedDispatch
} from '../../../../domain/cloud-topology';
import {DispatchService} from '../../../../services/vpp/dispatch.service';
import {takeUntil} from 'rxjs/operators';
import moment from 'moment-timezone';
import {ProgramService} from '../../../../services/vpp/program.service';
import {BaseRefreshComponent} from '../../../shared/base-refresh/base-refresh.component';
import {CustomRangeParam, RangePresetParam} from "../../../../domain/stats";
import {DISPATCH_TIME_BOUNDARY} from "../../../../domain/constants";

@Component({
    selector: 'app-dispatch-monitoring',
    templateUrl: './dispatch-monitoring.component.html',
    styleUrls: ['./dispatch-monitoring.component.css'],
    standalone: false
})
export class DispatchMonitoringComponent extends BaseRefreshComponent implements OnInit, OnChanges, OnDestroy {

  @Input() program: Program;
  activeDispatch: Dispatch;
  dispatches: Dispatch[];
  programSites: ProgramSite[];
  mapLocations: MapLocations[];
  selectedDispatch: ZonedDispatch = null;
  params: RangePresetParam | CustomRangeParam;
  timezone: string;
  shortZone: string;
  lastRefreshTime: string;
  loading: boolean;
  holdUpdates: boolean;
  refreshEnabled = false;
  noActiveDispatch = false;
  page = 1;
  pageSize = 5;

  private subject$: Subject<void>;
  constructor(private dispatchService: DispatchService,
              private programService: ProgramService) {
    super();
  }

  ngOnInit(): void {
    this.holdUpdates = false;
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.selectedDispatch = null;
    if (changes['program']['currentValue'] !== undefined) {
      this.program = changes['program']['currentValue'];
      this.programSites = [];
      this.mapLocations = [];
      this.setData();
    }
  }

  ngOnDestroy(): void {
    super.ngOnDestroy();

    this.subject$.next();
    this.subject$.complete();
  }

  setData(): void {
    if (this.program) {
      this.dispatches = [];
      this.lastRefreshTime = moment().format('MMM D YYYY H:mm:ss A');
      this.subject$ = new Subject<void>();

      this.programService.getProgramSites(this.program).pipe(takeUntil(this.subject$)).subscribe(programSites => {
        this.programSites = programSites;
        this.timezone = this.programSites[0]?.timezone;

        for (const site of this.programSites) {
          this.mapLocations.push({
            lat: site.latitude,
            long: site.longitude,
            description:  `${site.name} <br /> ${site.timezone}`
          });
        }

        if (this.timezone) {
          this.processDispatches();
        }

        if (this.programSites.length == 0) {
          this.timezone = 'UTC';
        }

      }, (error) => {
        console.error(error);
        if (!this.programSites) {
          this.timezone = 'UTC';
          this.processDispatches();
        }
      });
    }
  }

  private processDispatches() {
    this.dispatchService.getActiveDispatch(this.program).pipe(takeUntil(this.subject$)).subscribe(dispatch => {
      if (dispatch[0]) {
        this.activeDispatch = dispatch[0];
        this.activeDispatch.predictedOutput = 0;
        if (this.programSites.length) {
          this.activeDispatch = UIDispatch.convertFromUtc(this.activeDispatch, this.timezone);
          this.shortZone = moment(this.activeDispatch.startTimeUtc).tz(this.timezone, true).zoneAbbr();
        }

        this.dispatchService.getDispatchStatus(this.activeDispatch).pipe(takeUntil(this.subject$)).subscribe(dispatchStats => {
            this.activeDispatch.predictedOutput = dispatchStats.plannedPower.value;
        });

        this.dispatches.push(this.activeDispatch);
      }
    }, () => {
      this.noActiveDispatch = true;
      this.activeDispatch = null;
    });

    const futureTimeBoundary = moment().tz(this.timezone, true).add(DISPATCH_TIME_BOUNDARY, 'hours');
    const currentTimeBoundary = moment().tz(this.timezone, true);
    this.dispatchService.getProgramDispatches(this.program, this.timezone, ['PENDING'])
      .pipe(takeUntil(this.subject$)).subscribe(dispatches => {
      for (const pendingDispatch of dispatches) {
        const pendingDispatchStart = moment(pendingDispatch.startTimeUtc).tz(this.timezone, true);
        if (pendingDispatchStart.isBetween(currentTimeBoundary, futureTimeBoundary)) {
          if (!this.shortZone) {
            this.shortZone = moment(pendingDispatch.startTimeUtc).tz(this.timezone, true).zoneAbbr();
          }
          pendingDispatch.predictedOutput = 0;
          this.dispatchService.getDispatchStatus(pendingDispatch).pipe(takeUntil(this.subject$)).subscribe(dispatchStats => {
              pendingDispatch.predictedOutput = dispatchStats.plannedPower.value;
          });
          this.dispatches.push(pendingDispatch);
        }
      }
      this.holdUpdates = false;
    });
  }

  setSelectedDispatch($event: ZonedDispatch) {
    this.selectedDispatch = $event;
  }

  get filteredDispatches(): Dispatch[] {
    if (this.dispatches) {
      return this.dispatches
        .sort((a, b) => {
          return moment(b.startTimeUtc).valueOf() - moment(a.startTimeUtc).valueOf();
        })
        .map((dispatch, i) => ({id: i + 1, ...dispatch}))
        .slice((this.page - 1) * this.pageSize, (this.page - 1) * this.pageSize + this.pageSize);
    }
  }

  onParamChange($event: RangePresetParam | CustomRangeParam) {
    this.params = $event;
  }
}
